import {Fragment, useEffect, useState} from "react";
import {format} from "date-fns";
import {
    BreadCrumb,
    CenteredModal,
    Table,
    SkeletonTableLoading,
    FormInput,
    DropzoneFile,
    DropdownAction
} from "~/components";
import {faEdit, faEye, faTrash} from "@fortawesome/free-solid-svg-icons";
import featureAppAPI from "~/api/featureAppApi";
import {useFieldArray, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import commonApi from "~/api/commonApi";
import {Plus, Trash} from "lucide-react";

const breadCrumbItem = [
    { text: "Landing Page", to: "/ai-chatbot/administrator", isActive: false },
    { text: "Feature App", to: "", isActive: true },
]

function FeatureApp() {
    const [reloadTable, setReloadTable] = useState(false);
    const [featureApp, setFeatureApp] = useState({});
    const [loading, setLoading] = useState(true);
    const [featureAppDelete, setFeatureAppDelete] = useState({});
    const [mode, setMode] = useState("View");
    const [showActionModal, setShowActionModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const tableConfig = {
        columns: [
            {
                name: 'ID',
                width: '70px',
                className: 'fw-bold',
                key: 'FeatureAppId'
            },
            {
                name: 'TITLE',
                width: '200px',
                key: 'Title'
            },
            {
                name: 'DESCRIPTION',
                width: '300px',
                key: 'Description'
            },
            {
                name: 'MODIFIED BY',
                width: '150px',
                key: 'ModifiedBy'
            },
            {
                name: 'MODIFIED DATE',
                width: '150px',
                key: 'ModifiedDate',
                template: (row) => {
                    return (
                        <span>{format(new Date(row.ModifiedDate), "dd-MM-yyyy")}</span>
                    )
                }
            },
            {
                name: 'ACTION',
                width: '70px',
                key: 'Action',
                headerClassName: 'text-center',
                className: 'text-center',
                template: (row) => {
                    return (
                        <div className="dropdown d-inline-block">
                            <DropdownAction dropdownItems={[
                                { text: "View Details", icon: faEye, onClick: () => handleAction(row.FeatureAppId, "View") },
                                { text: "Edit", icon: faEdit, onClick: () => handleAction(row.FeatureAppId, "Edit") },
                                {text: "Delete", icon: faTrash, onClick: () => {
                                        setFeatureAppDelete({
                                            FeatureAppId: row.FeatureAppId,
                                            FileName: row.ThumbnailFileName,
                                        });
                                        setShowDeleteModal(true);
                                    }}
                            ]} isVerticalIcon={false}/>
                        </div>
                    )
                }
            }
        ],
        tableKey: "TableFeatureApp",
        hasShowHideColumn: false
    }

    useEffect(() => {
        setLoading(true)
        featureAppAPI.getAll()
            .then((res) => {
                setFeatureApp(res.data);
            })
            .catch(() => {
            })
            .finally(() => {
                setLoading(false);
            })
    }, [reloadTable]);

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors }
    } = useForm();

    const { fields, append, remove } = useFieldArray({
        control,
        name: "FeatureAppDescriptions"
    });


    const onSubmitFormFeature = (data) => {
        const { file, FeatureAppDescriptions, ...rest } = data;
        const formData = new FormData();

        Object.entries(rest).forEach(([key, value]) => {
            formData.append(key, value);
        });

        FeatureAppDescriptions.forEach((item, index) => {
            Object.entries(item).forEach(([key, value]) => {
                formData.append(`FeatureAppDescriptions[${index}].${key}`, value);
            });
        });

        if (file?.[0]) {
            formData.append("file", file[0]);
        }

        if(mode === "Add") {
            featureAppAPI.insert(formData)
                .then(res => {
                    res.data?.Success ?
                        toast.success(<span className="fs-9 fw-bold">Your feature app has been added.</span>)
                        : toast.error(<span className="fs-9 fw-bold">An error occurred while adding your feature app.</span>)
                })
                .catch(err => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while adding your feature app.</span>)
                })
                .finally(() => {
                    handleCloseModal();
                    setReloadTable(pre => !pre);
                })
        }
        else {
            featureAppAPI.update(formData)
                .then(res => {
                    res.data?.Success ?
                        toast.success(<span className="fs-9 fw-bold">Your feature app has been updated.</span>)
                        : toast.error(<span className="fs-9 fw-bold">An error occurred while updating your feature app.</span>)
                })
                .catch(err => {
                    toast.error(<span className="fs-9 fw-bold">An error occurred while updating your feature app.</span>)

                })
                .finally(() => {
                    handleCloseModal();
                    setReloadTable(pre => !pre);
                })
        }
    }

    const handleAction = (featureAppId, mode) => {
        setMode(mode);
        featureAppAPI.getById(featureAppId)
            .then(async (res) => {
                reset(res.data)
                if(mode !== "Add") {
                    const fileName = res.data.ThumbnailFileName;
                    await commonApi.getUploadFile(fileName)
                        .then((res) => {
                            const file = new File([res.data], fileName, { type: res.data.type });
                            const preview = URL.createObjectURL(file);
                            const newFile = {
                                name: file.name,
                                size: (file.size / 1024).toFixed(2) + " KB",
                                preview,
                                file,
                            };
                            setSelectedFile(newFile);
                        })
                        .catch(() => {
                        });
                }
                setShowActionModal(true);
            })
            .catch(() => {})
    }

    const handleAddDescription = () => {
        append({ FeatureAppDescriptionId: 0, FeatureAppId: 0, Description: "" });
    }

    const handleCloseModal = () => {
        setShowActionModal(false);
        setSelectedFile(null);
    }

    const handleConfirmDelete = () => {
        featureAppAPI.delete(featureAppDelete.FeatureAppId, featureAppDelete.FileName)
            .then((res) => {
                res.data?.Success ?
                    toast.success(<span className="fs-9 fw-bold">Your feature app has been deleted.</span>)
                    : toast.error(<span className="fs-9 fw-bold">An error occurred while deleting your feature app.</span>)
            })
            .catch(() => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while deleting your feature app.</span>)
            })
            .finally(() => {
                setShowDeleteModal(false);
                setReloadTable(pre => !pre);
            })
    }

    return (
        <Fragment>
            <BreadCrumb breadCrumbItem={breadCrumbItem}/>
            <h2 className="text-bold text-body-emphasis mb-5">Feature App</h2>
            <button className="btn btn-bt-primary mb-4" type="button" onClick={() => handleAction(0, "Add")}>
                Add New Feature App <Plus size={15}/>
            </button>
            {loading ? (
                <SkeletonTableLoading columns={tableConfig.columns} rows={10} />
            ) : (
                <Table tableConfig={{...tableConfig, data : featureApp}}/>
            )}
            <CenteredModal
                show={showActionModal}
                onClose={handleCloseModal}
                title= {mode + " Feature App"}
                formId="formActionFeatureApp"
                isViewMode={mode === "View"}
                closeText={mode === "View" ? "Close" : "Cancel"}
                modalType="modal-lg"
                modalHeight="750px"
            >
                <form onSubmit={handleSubmit(onSubmitFormFeature)} id="formActionFeatureApp">

                    <div>
                        <div className="mb-3 nav nav-underline" role="tablist">
                            <div className="nav-item">
                                <a
                                    className="nav-link active"
                                    id="tab-main-tab"
                                    data-bs-toggle="tab"
                                    href="#tab-main"
                                    role="tab"
                                    aria-controls="tab-main"
                                    aria-selected="true"
                                >
                                    Main
                                </a>
                            </div>
                            <div className="nav-item">
                                <a
                                    className="nav-link"
                                    id="tab-mapping-tab"
                                    data-bs-toggle="tab"
                                    href="#tab-mapping"
                                    role="tab"
                                    aria-controls="tab-mapping"
                                    aria-selected="false"
                                >
                                    Assign Description
                                </a>
                            </div>
                        </div>
                        <div className="tab-content">
                            <div
                                className="tab-pane fade show active"
                                id="tab-main"
                                role="tabpanel"
                                aria-labelledby="tab-main-tab"
                            >
                                <div className="mb-3">
                                    <FormInput showHeaderLabel={true}
                                               name = "Title"
                                               errors={errors}
                                               required={true}
                                               label="Assign Title"
                                               validation={{...register("Title", {
                                                       required: "Title is required."
                                                   })}}
                                               disabled={mode === "View"}
                                    />
                                </div>
                                <div className="mb-3">
                                    <FormInput showHeaderLabel={true}
                                               name = "Description"
                                               errors={errors}
                                               textarea={true}
                                               required={true}
                                               label="Assign Description"
                                               validation={{...register("Description", {
                                                       required: "Description is required."
                                                   })}}
                                               disabled={mode === "View"}
                                    />
                                </div>
                                <div className="mb-3">
                                    <DropzoneFile
                                        label="Assign Thumbnail(*)"
                                        register={register}
                                        name="file"
                                        errors={errors}
                                        selectedFile={selectedFile}
                                        setSelectedFile={setSelectedFile}
                                        disabled={mode === "View"}
                                    />
                                </div>
                            </div>
                            <div
                                className="tab-pane fade"
                                id="tab-mapping"
                                role="tabpanel"
                                aria-labelledby="tab-mapping-tab"
                            >
                                {
                                    fields.map((item, index) => {
                                        return (
                                            <div className="mb-3" key={index}>
                                                {
                                                    mode === "View" &&
                                                    (
                                                        <FormInput
                                                            label="Assign Description"
                                                            name={`FeatureAppDescriptions[${index}].Description`}
                                                            errors={errors}
                                                            disabled={true}
                                                            required={true}
                                                            defaultValue={item.Description}
                                                            maxLengthText = "max length 250 character"
                                                            showHeaderLabel={false}
                                                            validation={{...register(`FeatureAppDescriptions[${index}].Description`, {
                                                                    required: "Description is required.",
                                                                })}}
                                                        />
                                                    )
                                                }
                                                {
                                                    mode !== "View" && (
                                                        <div className="row">
                                                            <div className="col-sm-11 col-12">
                                                                <FormInput
                                                                    label="Assign Description"
                                                                    name={`FeatureAppDescriptions[${index}].Description`}
                                                                    errors={errors}
                                                                    required={true}
                                                                    defaultValue={item.Description}
                                                                    maxLengthText = "max length 250 character"
                                                                    showHeaderLabel={false}
                                                                    validation={{...register(`FeatureAppDescriptions[${index}].Description`, {
                                                                            required: "Description is required.",
                                                                        })}}
                                                                />
                                                            </div>
                                                            <div className="col-sm-1 col-12">
                                                                <Trash size={15} onClick={() => remove(index)} className="mt-3 text-danger cursor-pointer" />
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        )
                                    })
                                }
                                {
                                    mode !== "View" && (
                                        <div className="text-center">
                                            <button
                                                className="btn btn-bt-primary ms-4"
                                                type="button"
                                                onClick={handleAddDescription}
                                            >
                                                Add New Description <Plus size={15}/>
                                            </button>
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                </form>
            </CenteredModal>
            <CenteredModal show={showDeleteModal}
                           onClose={()=> setShowDeleteModal(false)}
                           onConfirm={handleConfirmDelete}
                           title="Delete Feature App"
            >
                <div className="text-center">
                    <span className="fs-8-1 fw-semibold">Are you sure you want to delete this feature app?</span>
                </div>
            </CenteredModal>
        </Fragment>
    )
}
export default FeatureApp;